import { useState } from "react";

import { IgDivider } from "@intelligems/ig-theme/IgDivider";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import IgMobileDialog from "@intelligems/ig-theme/mobile/IgMobileDialog";
import { VariationFe } from "@intelligems/ig-types/fe-only/variations";
import { Box, Stack, useTheme } from "@mui/material";
import { priceTestIntegrationGettingStarted } from "@src/utils/linkSheet";
import { openNewTab } from "@src/utils/openNewTab";

import { MobileVariationCard } from "./shared/MobileVariationCard";

interface MobilePriceActionModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (variationId: string | null) => void;
  variations: VariationFe[];
  action: "pause" | "end";
  loading?: boolean;
  allowSkipUpdate?: boolean;
}

export const MobilePriceActionModal = ({
  open,
  onClose,
  onConfirm,
  variations,
  action,
  loading = false,
  allowSkipUpdate = false,
}: MobilePriceActionModalProps) => {
  const theme = useTheme();
  const SKIP_UPDATE_ID = "SKIP_UPDATE";
  const [selectedVariationId, setSelectedVariationId] = useState<string | null>(
    null
  );

  const withoutUpdate = selectedVariationId === SKIP_UPDATE_ID;
  const actionVerb = action === "pause" ? "Pause" : "End";

  const handleClose = () => {
    setSelectedVariationId(null);
    onClose();
  };

  const handleConfirm = () => {
    onConfirm(
      selectedVariationId === SKIP_UPDATE_ID ? null : selectedVariationId
    );
  };

  const isConfirmDisabled = !selectedVariationId;

  return (
    <IgMobileDialog
      open={open}
      onClose={handleClose}
      title={`${actionVerb} Price Test`}
      subtitle={
        <Box>
          <IgTypography>
            Which group's prices should be applied to Shopify?
          </IgTypography>
          <IgTypography
            sx={{
              color: theme.igPrimitives.base[500],
              cursor: "pointer",
              pl: 1,
            }}
            onClick={() => openNewTab(priceTestIntegrationGettingStarted)}
          >
            Find out more.
          </IgTypography>
        </Box>
      }
      yesButtonText={
        withoutUpdate
          ? `${actionVerb} without updating prices`
          : `${actionVerb} & apply prices`
      }
      yesButtonColor={action === "pause" ? "primary" : "error"}
      handleYesClick={handleConfirm}
      handleNoClick={handleClose}
      yesButtonDisabled={isConfirmDisabled}
      loading={loading}
    >
      {/* Variation Cards */}
      <Stack spacing={4}>
        {variations.map((variation) => (
          <MobileVariationCard
            key={variation.id}
            variation={{
              id: variation.id,
              name: variation.name,
              isControl: variation.isControl,
            }}
            selected={selectedVariationId === variation.id}
            onSelect={(id) => setSelectedVariationId(id)}
          />
        ))}
      </Stack>
      {/* Don't Update Prices Option (Admin Only) */}
      {allowSkipUpdate && (
        <>
          <IgDivider />
          <MobileVariationCard
            variation={{
              id: SKIP_UPDATE_ID,
              name: "Don't update prices in Shopify",
              isControl: false,
            }}
            selected={selectedVariationId === SKIP_UPDATE_ID}
            onSelect={(id) => setSelectedVariationId(id)}
          />
        </>
      )}
    </IgMobileDialog>
  );
};
